import { RefreshCcw, ShieldCheck, XCircle } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import SortableTable from '../components/SortableTable';
import useAdminAction from '../hooks/useAdminAction';
import useAdminRole from '../hooks/useAdminRole';
import { ADMIN_ACTIONS } from '../constants/adminActions';
import { adminTaskRef, fetchAdminTasks } from '../services/firestore';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending_approval', label: 'Needs approval' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

function formatTime(value) {
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function taskType(task) {
  return task.type || task.taskType || task.action || '-';
}

function targetLabel(task) {
  return task.targetBusinessId || task.targetId || task.target || '-';
}

function statusClass(status) {
  if (['completed', 'approved'].includes(status)) return 'is-online';
  if (['failed', 'rejected', 'cancelled'].includes(status)) return 'is-offline';
  return 'is-pending';
}

function TaskDecisionDialog({
  task,
  mode,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
  loading,
}) {
  if (!task || !mode) return null;
  const highRisk = mode === 'approve' || mode === 'reject';

  return (
    <div className="wb-modal-backdrop">
      <div className={`wb-modal ${highRisk ? 'risk-high' : ''}`}>
        <h3>{mode === 'approve' ? 'Approve task?' : mode === 'reject' ? 'Reject task?' : mode === 'cancel' ? 'Cancel task?' : 'Retry task?'}</h3>
        <p className="wb-muted">{taskType(task)} / {targetLabel(task)}</p>
        <label className="wb-field">
          <span>Reason <strong className="wb-required">*</strong></span>
          <textarea
            rows={4}
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            placeholder="Record the operational reason for this decision"
          />
        </label>
        <div className="wb-modal-actions">
          <button type="button" className="secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            type="button"
            className={highRisk || mode === 'cancel' ? 'danger' : ''}
            onClick={onConfirm}
            disabled={loading || !reason.trim()}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TaskQueue() {
  const { isSuperAdmin, can } = useAdminRole();
  const { performAction, loading: actionLoading, error: actionError } = useAdminAction();
  const [status, setStatus] = useState('pending_approval');
  const [type, setType] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [decisionMode, setDecisionMode] = useState('');
  const [reason, setReason] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchAdminTasks({ status, type });
      setRows(data.tasks);
    } catch (error) {
      setLoadError(error?.message || 'Could not load admin tasks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status, type]);

  const taskTypes = useMemo(() => (
    [...new Set(rows.map(taskType).filter((item) => item && item !== '-'))].sort()
  ), [rows]);

  const openDecision = (task, mode) => {
    setSelectedTask(task);
    setDecisionMode(mode);
    setReason('');
  };

  const closeDecision = () => {
    setSelectedTask(null);
    setDecisionMode('');
    setReason('');
  };

  const updateTask = async () => {
    if (!selectedTask || !decisionMode || !reason.trim()) return;

    const nextStatus = {
      approve: 'approved',
      reject: 'rejected',
      cancel: 'cancelled',
      retry: 'pending',
    }[decisionMode];
    const action = {
      approve: ADMIN_ACTIONS.APPROVAL_APPROVED,
      reject: ADMIN_ACTIONS.APPROVAL_REJECTED,
      cancel: ADMIN_ACTIONS.ADMIN_TASK_CANCELLED,
      retry: ADMIN_ACTIONS.ADMIN_TASK_RETRIED,
    }[decisionMode];

    await performAction({
      action,
      targetType: 'adminTask',
      targetId: selectedTask.id,
      before: { status: selectedTask.status },
      after: { status: nextStatus },
      reason,
      severity: decisionMode === 'approve' || decisionMode === 'reject' ? 'CRITICAL' : 'WARNING',
      writeFn: (batch, batchDb) => {
        batch.set(adminTaskRef(batchDb, selectedTask.id), {
          status: nextStatus,
          decisionReason: reason.trim(),
          decidedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          ...(decisionMode === 'retry' ? { retryCount: Number(selectedTask.retryCount || 0) + 1 } : {}),
        }, { merge: true });
      },
    });

    closeDecision();
    await load();
  };

  const canMutateTasks = isSuperAdmin && can('approvals');

  const columns = [
    { key: 'type', header: 'Task type', accessor: taskType },
    { key: 'target', header: 'Target', accessor: targetLabel },
    { key: 'createdBy', header: 'Created by', accessor: (row) => row.createdBy || row.requestedBy || '-' },
    { key: 'createdAt', header: 'Created at', accessor: 'createdAt', render: (row) => formatTime(row.createdAt) },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      render: (row) => <span className={`wb-status-pill ${statusClass(row.status)}`}>{row.status || '-'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: 'status',
      render: (row) => (
        <div className="wb-row-actions">
          <button type="button" className="secondary" onClick={() => setSelectedTask(row)}>View</button>
          {row.status === 'pending_approval' ? (
            <>
              <button type="button" disabled={!canMutateTasks} onClick={() => openDecision(row, 'approve')}><ShieldCheck size={14} /> Approve</button>
              <button type="button" className="danger" disabled={!canMutateTasks} onClick={() => openDecision(row, 'reject')}>Reject</button>
            </>
          ) : null}
          {row.status === 'failed' ? (
            <button type="button" disabled={!canMutateTasks} onClick={() => openDecision(row, 'retry')}><RefreshCcw size={14} /> Retry</button>
          ) : null}
          {['pending', 'pending_approval'].includes(row.status) ? (
            <button type="button" className="danger" disabled={!canMutateTasks} onClick={() => openDecision(row, 'cancel')}><XCircle size={14} /> Cancel</button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="wb-stack">
      {!canMutateTasks ? (
        <div className="wb-warning-banner">Read-only: only super_admin users can approve, reject, cancel, or retry admin tasks.</div>
      ) : null}

      <section className="wb-card wb-card--elevated">
        <div className="wb-card-head">
          <div>
            <h3 className="wb-section-title">Admin Task Queue</h3>
            <p className="wb-muted">Approval-required work, broadcasts, cache jobs, GST publishes, and failed background operations.</p>
          </div>
          <button type="button" onClick={load} disabled={loading}><RefreshCcw size={16} /> Refresh</button>
        </div>

        <div className="wb-toolbar-grid">
          <label className="wb-field">
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {STATUS_FILTERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="wb-field">
            <span>Task type</span>
            <select value={type} onChange={(event) => setType(event.target.value)}>
              <option value="">All types</option>
              {taskTypes.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label className="wb-field">
            <span>Filter</span>
            <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search task, target, admin" />
          </label>
        </div>
      </section>

      <section className="wb-card">
        {loading ? (
          <div className="wb-skeleton wb-skeleton--table" />
        ) : (
          <SortableTable
            rows={rows}
            columns={columns}
            filter={filter}
            emptyText="No admin tasks match the current filters."
          />
        )}
        {loadError ? <p className="wb-error-text">{loadError}</p> : null}
        {actionError ? <p className="wb-error-text">{actionError}</p> : null}
      </section>

      {selectedTask && !decisionMode ? (
        <div className="wb-drawer-backdrop">
          <aside className="wb-drawer wb-task-drawer">
            <div className="wb-card-head">
              <div>
                <h3 className="wb-section-title">{taskType(selectedTask)}</h3>
                <p className="wb-muted">{selectedTask.id}</p>
              </div>
              <button type="button" className="secondary" onClick={() => setSelectedTask(null)}>Close</button>
            </div>
            <div className="wb-kpi-grid">
              <div className="wb-kpi"><span>Status</span><strong>{selectedTask.status || '-'}</strong></div>
              <div className="wb-kpi"><span>Severity</span><strong>{selectedTask.severity || 'INFO'}</strong></div>
              <div className="wb-kpi"><span>Created</span><strong>{formatTime(selectedTask.createdAt)}</strong></div>
            </div>
            <div>
              <strong>Reason</strong>
              <p className="wb-muted">{selectedTask.reason || selectedTask.decisionReason || '-'}</p>
            </div>
            <div>
              <strong>Payload</strong>
              <pre className="wb-cell-json">{JSON.stringify(selectedTask.payload || selectedTask.after || selectedTask, null, 2)}</pre>
            </div>
          </aside>
        </div>
      ) : null}

      <TaskDecisionDialog
        task={selectedTask}
        mode={decisionMode}
        reason={reason}
        onReasonChange={setReason}
        onClose={closeDecision}
        onConfirm={updateTask}
        loading={actionLoading}
      />
    </div>
  );
}
